import { FC, Fragment, useContext, useMemo, useState } from 'react';
import { pickBy, size } from 'lodash-es';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';

import { useGenerateReport } from '~/core/shared/api/middle-office/report';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useCan } from '~/core/shared/hooks/useCan';
import { handleError, handleSuccess } from '~/core/shared/utils/admin';
import { createFilterParams } from '~/core/shared/utils/queries';
import { AssignmentQueueButtons } from '../../filters/AssignmentQueueButtons.component';
import { ClearFiltersButton } from '../../filters/ClearFiltersButton.component';
import { DocumentsSearchInput } from '../../filters/DocumentsSearchInput.component';
import { MyDealsOnlyButton } from '../../filters/MyDealsOnlyButton.component';
import { OpenFiltersButton } from '../../filters/OpenFiltersButton.component';
import { SaveFilterButton } from '../../filters/SaveFilterButton.component';
import { DocumentListContext, DocumentListViewContext } from '../List.view';

export const ApplicationsSubheader: FC = () => {
  const { generateReport, isPending: isReportLoading } = useGenerateReport();
  const { filters } = useContext(DocumentListContext);
  const { config } = useContext(DocumentListViewContext);
  const { can } = useCan();

  const handleGenerateReports = async (): Promise<void> => {
    const params = createFilterParams(filters);

    await generateReport({ type: config.report ?? 'default', params })
      .then(() => handleSuccess('Report will be sent to your email'))
      .catch(() => handleError('Error creating report'));
  };

  const filtersCount = useMemo(() => {
    const filtersTemp = structuredClone(filters);

    for (const [key, value] of Object.entries(filtersTemp)) {
      if (Array.isArray(value) && value.length < 1) {
        delete filtersTemp[key];
      }
      if (key === 'application_submitted' || key === 'is_pre_application') {
        // remove this from count as it gets duplicated with from - to filters
        // remove is_pre_application since its an 'invisible' filter and its applied always
        delete filtersTemp[key];
      }
    }

    return size(pickBy(filtersTemp));
  }, [filters]);

  return (
    <Flex
      alignItems="center"
      bg="gray.50"
      borderBottom={1}
      borderColor="gray.200"
      borderStyle="solid"
      color="gray.600"
      justifyContent="space-between"
      minH="60px"
      px={4}
      py={2}
    >
      <Stack direction="row" gap={4}>
        <DocumentsSearchInput />

        <MyDealsOnlyButton />

        {config.customFilters?.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>{item}</Fragment>
        ))}

        <OpenFiltersButton filtersCount={filtersCount} />

        <SaveFilterButton />

        {filtersCount !== 0 && <ClearFiltersButton />}

        <AssignmentQueueButtons />
      </Stack>

      {config.report && can(Permissions.ReportsCreate) && (
        <Button
          colorPalette="primary"
          loading={isReportLoading}
          onClick={handleGenerateReports}
          variant="solid"
        >
          <Text>Generate report</Text>
        </Button>
      )}
    </Flex>
  );
};
